import React, { useEffect } from 'react';
import {
  AppBar,
  Box,
  Drawer,
  IconButton,
  List,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Toolbar,
  useMediaQuery,
  useTheme,
  Tooltip,
  styled
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Folder as FolderIcon,
  Home as HomeIcon,
  LibraryBooks as ResourcesIcon,
  InsertChart as InsightsIcon,
  Gavel as ComplianceIcon,
  School as TrainingIcon,
  Event as EventIcon,
  Description as FormsIcon,
  ChevronRight as ChevronRightIcon,
  ChevronLeft as ChevronLeftIcon,
  MeetingRoom as MeetingRoomIcon
} from '@mui/icons-material';
import { Link, useLocation } from 'react-router-dom';
import { DateTime } from './DateTime';
import { useNavigationStore } from '../store/navigationStore';

const DRAWER_WIDTH = 240;
const COLLAPSED_DRAWER_WIDTH = 72;

const menuItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
  { text: 'Students', icon: <PeopleIcon />, path: '/students' },
  { text: 'Cases', icon: <FolderIcon />, path: '/cases' },
  { text: 'Events', icon: <EventIcon />, path: '/events' },
  { text: 'Meetings', icon: <MeetingRoomIcon/>, path: '/meetings'},
  { text: 'Home Visits', icon: <HomeIcon />, path: '/visits' },
  // { text: 'Survey Builder', icon: <FormsIcon />, path: '/forms' },
  // { text: 'Forms', icon: <FormsIcon />, path: '/forms' },
  { text: 'Resources', icon: <ResourcesIcon />, path: '/resources' },
  { text: 'Insights', icon: <InsightsIcon />, path: '/insights' },
  { text: 'Compliance', icon: <ComplianceIcon />, path: '/compliance' },
  { text: 'Training', icon: <TrainingIcon />, path: '/training' }
];

const StyledDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== 'isExpanded'
})<{ isExpanded: boolean }>(({ theme, isExpanded }) => ({
  width: isExpanded ? DRAWER_WIDTH : COLLAPSED_DRAWER_WIDTH,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  '& .MuiDrawer-paper': {
    width: isExpanded ? DRAWER_WIDTH : COLLAPSED_DRAWER_WIDTH,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    }),
    overflowX: 'hidden',
    backgroundColor: theme.palette.background.paper,
    borderRight: `1px solid ${theme.palette.divider}`
  }
}));

export const Navigation: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const location = useLocation();
  const { isMenuExpanded, toggleMenu } = useNavigationStore();

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.altKey && e.key.toLowerCase() === 'm') {
        toggleMenu();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [toggleMenu]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const drawer = (
    <>
      <Toolbar sx={{ justifyContent: 'space-between' }}>
        <Box component="div" sx={{ 
          typography: 'h6', 
          fontWeight: 500,
          opacity: isMenuExpanded ? 1 : 0,
          transition: theme.transitions.create('opacity')
        }}>
          PSA Counselor
        </Box>
        {!isMobile && (
          <Tooltip title={isMenuExpanded ? "Collapse menu" : "Expand menu"}>
            <IconButton 
              onClick={toggleMenu}
              sx={{
                bgcolor: 'background.paper',
                '&:hover': {
                  bgcolor: 'action.hover'
                },
                boxShadow: 1,
                position: 'absolute',
                right: isMenuExpanded ? 8 : '50%',
                transform: isMenuExpanded ? 'none' : 'translateX(50%)'
              }}
            >
              {isMenuExpanded ? <ChevronLeftIcon /> : <ChevronRightIcon />}
            </IconButton>
          </Tooltip>
        )}
      </Toolbar>
      <List sx={{ px: 2 }}>
        {menuItems.map((item) => (
          <ListItemButton
            key={item.text}
            component={Link}
            to={item.path}
            selected={location.pathname === item.path}
            onClick={() => isMobile && handleDrawerToggle()}
            sx={{
              minHeight: 48,
              justifyContent: isMenuExpanded ? 'initial' : 'center',
              px: 2.5,
              borderRadius: 1,
              mb: 0.5,
              '&.Mui-selected': {
                bgcolor: 'primary.light',
                '&:hover': {
                  bgcolor: 'primary.light'
                }
              }
            }}
          >
            <Tooltip title={!isMenuExpanded ? item.text : ''} placement="right">
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: isMenuExpanded ? 2 : 'auto',
                  justifyContent: 'center',
                  color: location.pathname === item.path ? 'primary.main' : 'inherit'
                }}
              >
                {item.icon}
              </ListItemIcon>
            </Tooltip>
            <ListItemText
              primary={item.text}
              primaryTypographyProps={{
                fontSize: '0.875rem',
                fontWeight: location.pathname === item.path ? 500 : 400,
                display: isMenuExpanded ? 'block' : 'none'
              }}
            />
          </ListItemButton>
        ))}
      </List>
    </>
  );

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { sm: `calc(100% - ${isMenuExpanded ? DRAWER_WIDTH : COLLAPSED_DRAWER_WIDTH}px)` },
          ml: { sm: `${isMenuExpanded ? DRAWER_WIDTH : COLLAPSED_DRAWER_WIDTH}px` },
          bgcolor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen
          })
        }}
      >
        <Toolbar sx={{ px: { xs: 2, sm: 3 } }}>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
            aria-label="open drawer"
          >
            <MenuIcon />
          </IconButton>
          <Box
            component="div"
            sx={{
              typography: 'h6',
              fontWeight: 500,
              flexGrow: 1,
              color: 'text.primary'
            }}
          >
            PSA Counselor System
          </Box>
          <DateTime />
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{
          width: { sm: isMenuExpanded ? DRAWER_WIDTH : COLLAPSED_DRAWER_WIDTH },
          flexShrink: { sm: 0 }
        }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true
          }}
          sx={{
            display: { xs: 'block', sm: 'none' }
          }}
        >
          {drawer}
        </Drawer>
        <StyledDrawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' }
          }}
          open
          isExpanded={isMenuExpanded}
        >
          {drawer}
        </StyledDrawer>
      </Box>
    </>
  );
};